import { ColumnsType } from 'antd/es/table/Table';
import config from 'config';
import { Routes } from 'config/routes';
import { format, differenceInDays } from 'date-fns';
import {
  BloodType,
  DonorListFilterInput,
  DonorListSort,
  DonorOutput,
} from 'gql/types';
import { Link } from 'modules/common/components';
import { ISortOption } from 'modules/common/interfaces';
import { createSortOrderFactory } from 'modules/common/utils/sort/createSortOrderFactory';
import { getTranslation } from 'utils/getTranslation';

import { DonorNotesCell } from '../DonorNotesCell';

const t = getTranslation('donor.ReserveDonorsTable');

const bloodTypes = Object.values(BloodType);

type PropTypes = {
  addNewNoteHandler: (donorId: string) => void;
  updateNoteHandler: (noteId: string, previousValue: string) => void;
  deleteNoteHandler: (noteId: string) => void;
  showSorterTooltip?: boolean;
  sort?: ISortOption<DonorListSort>;
  filter?: DonorListFilterInput;
};

export const getColumns = ({
  addNewNoteHandler,
  updateNoteHandler,
  deleteNoteHandler,
  showSorterTooltip,
  sort,
  filter,
}: PropTypes): ColumnsType<DonorOutput> => {
  const getDefaultSort = createSortOrderFactory<DonorListSort>(sort);
  return [
    {
      title: t('donorId'),
      render: (row: DonorOutput) => row.shortId,
      align: 'center',
    },
    {
      title: t('petName'),
      key: DonorListSort.Name,
      sorter: true,
      sortOrder: getDefaultSort(DonorListSort.Name),
      showSorterTooltip,
      render: (donor: DonorOutput) => (
        <Link
          to={`${Routes.DonorsProfile.replace(':id', String(donor.shortId))}`}
        >
          {donor.name}
        </Link>
      ),
      align: 'center',
    },
    {
      title: t('ownerLastName'),
      key: DonorListSort.LastName,
      sorter: true,
      sortOrder: getDefaultSort(DonorListSort.LastName),
      showSorterTooltip,
      render: (row: DonorOutput) => row.user?.lastName,
      align: 'center',
    },
    {
      title: t('bloodType'),
      render: (row: DonorOutput) =>
        row.bloodType && t(row.bloodType.toLowerCase()),
      filters: bloodTypes.map((bloodType) => {
        return {
          text: t(bloodType.toLowerCase()),
          value: bloodType,
        };
      }),
      align: 'center',
      filteredValue: filter?.bloodType || null,
    },
    {
      title: t('enteredDate'),
      key: DonorListSort.ReservedDate,
      sorter: true,
      sortOrder: getDefaultSort(DonorListSort.ReservedDate),
      showSorterTooltip,
      render: (row: DonorOutput) =>
        format(new Date(row.becameReservedDonorDate), config.DATE_FORMATS.DATE),
      align: 'center',
    },
    {
      title: t('daysInReserve'),
      render: (row: DonorOutput) =>
        differenceInDays(new Date(), new Date(row.becameReservedDonorDate)),
      align: 'center',
    },
    {
      title: t('notes'),
      key: 'notes',
      width: 320,
      render: (row: DonorOutput) => (
        <DonorNotesCell
          notes={row.reserveNotes || []}
          onAdd={() => addNewNoteHandler(row.id)}
          onEdit={(noteId, text) => updateNoteHandler(noteId, text)}
          onDelete={(noteId) => deleteNoteHandler(noteId)}
        />
      ),
      align: 'center',
    },
  ];
};
